/**
 * Content items resource hooks. Mirrors the API endpoints from
 * `api/src/content/content.routes.ts`:
 *
 *   - GET    /api/admin/sites/:siteId/content-items   — paginated list
 *   - POST   /api/admin/sites/:siteId/content-items   — create new item
 *   - GET    /api/admin/content-items/:id             — detail
 *   - DELETE /api/admin/content-items/:id             — soft-delete
 *   - POST   /api/admin/content-items/:id/versions    — create draft version
 *   - POST   /api/admin/content-items/:id/versions/draft-from/:sourceVersionId
 *                                                     — rollback / amend
 *   - PATCH  /api/admin/content-versions/:id          — edit DRAFT version
 *   - POST   /api/admin/content-versions/:id/publish  — flip DRAFT → PUBLISHED
 *   - POST   /api/admin/content-versions/:id/unpublish — flip PUBLISHED → ARCHIVED
 *
 * #19 (AI Studio) and #173 (standalone admin) both consume these hooks.
 * AI Studio uses the list query; the standalone admin uses every endpoint
 * via the four routes mounted in `router.tsx`.
 *
 * Every mutation invalidates the matching detail / list query keys so the
 * SPA never reads stale state after a write. 409 VERSION_RACE is surfaced
 * as a typed `ApiClientError` and handled by the call site (toast +
 * explicit refetch), exactly like the page-version equivalents in
 * `usePageVersions.ts`.
 */

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import type {
  ArchiveExperimentContentVersionResponse,
  ContentItemResponse,
  ContentItemWithVersionsResponse,
  ContentVersionResponse,
  CreateContentItemInput,
  PromoteContentVersionResponse,
  PublishContentVersionResponse,
  UnpublishContentVersionResponse,
  UpdateContentVersionInput,
} from "@ottopilot/shared";
import { apiDelete, apiGet, apiList, apiPatch, apiPost } from "../lib/api";
import type { ApiClientError, PageMeta } from "../lib/api";
import { queryKeys } from "./queryKeys";

export type ContentItemsListResult = {
  data: ContentItemResponse[];
  meta: PageMeta | undefined;
};

export function useContentItemsForSite(
  siteId: string | undefined,
  params: { page: number; pageSize: number },
) {
  return useQuery<ContentItemsListResult, ApiClientError>({
    queryKey: queryKeys.contentItems.listForSite(siteId ?? "", params),
    queryFn: () =>
      apiList<ContentItemResponse[]>(`/api/admin/sites/${siteId ?? ""}/content-items`, {
        query: { page: params.page, pageSize: params.pageSize },
      }),
    enabled: Boolean(siteId),
    placeholderData: keepPreviousData,
  });
}

/**
 * Fetch the ContentItem detail (the item identity row + its latest draft,
 * currently-published version, and archived versions). Disabled when
 * `id` is falsy so the query doesn't fire on an in-flight route mount
 * with a missing param.
 */
export function useContentItem(id: string | undefined) {
  return useQuery<ContentItemWithVersionsResponse, ApiClientError>({
    queryKey: queryKeys.contentItems.detail(id ?? ""),
    queryFn: () => apiGet<ContentItemWithVersionsResponse>(`/api/admin/content-items/${id ?? ""}`),
    enabled: Boolean(id),
  });
}

/**
 * `POST /api/admin/sites/:siteId/content-items` — create a new
 * ContentItem. Invalidates the list-for-site key so the list page
 * refetches with the new row included. The hook does NOT auto-create
 * a v1 ContentVersion — the slice-1 / slice-2 contract is that items
 * and versions are independently created.
 */
export function useCreateContentItem(siteId: string) {
  const qc = useQueryClient();
  return useMutation<ContentItemResponse, ApiClientError, CreateContentItemInput>({
    mutationFn: (input) =>
      apiPost<ContentItemResponse>(`/api/admin/sites/${siteId}/content-items`, input),
    onSuccess: () => {
      // Wide invalidation — we don't know which page+pageSize the caller
      // was looking at, so invalidate every list key for this site.
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.all });
    },
  });
}

/**
 * `DELETE /api/admin/content-items/:id` — soft-delete a ContentItem.
 * The item's versions stay intact for audit; subsequent renders of any
 * page that references it surface a `MissingBlock` placeholder rather
 * than crashing. Invalidates the list so the row disappears.
 */
export function useDeleteContentItem(siteId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, { id: string }>({
    mutationFn: ({ id }) => apiDelete(`/api/admin/content-items/${id}`),
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.all });
      // Also bust the per-item detail so a stale tab on the detail page
      // refetches and reflects the deletion.
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.detail(id) });
      // Pages that referenced this item now see a MissingBlock placeholder
      // — the renderer reads this server-side, no client cache to bust.
      void siteId;
    },
  });
}

/**
 * `POST /api/admin/content-items/:id/versions` — create the next DRAFT
 * version for an item. Used when the item has no draft yet (e.g. a
 * freshly-created item — the slice-1 / slice-2 contract is that items
 * are created without an initial version). The body carries the
 * `contentJson` payload the new version starts with.
 */
export function useCreateContentVersion(contentItemId: string) {
  const qc = useQueryClient();
  return useMutation<
    ContentVersionResponse,
    ApiClientError,
    { contentJson: unknown; styleJson?: unknown; aiMetaJson?: unknown }
  >({
    mutationFn: (input) =>
      apiPost<ContentVersionResponse>(`/api/admin/content-items/${contentItemId}/versions`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.detail(contentItemId) });
    },
  });
}

/**
 * `PATCH /api/admin/content-versions/:id` — patch a DRAFT version's
 * `contentJson` / `styleJson` / `aiMetaJson`. The server rejects PATCHes
 * against non-DRAFT rows with 422 `VERSION_NOT_DRAFT`; the UI hides the
 * save button on those slots so the rejection is defence in depth.
 *
 * Takes the parent `contentItemId` at hook construction so the success
 * invalidation targets the right detail key.
 */
export function useUpdateContentVersion(contentItemId: string) {
  const qc = useQueryClient();
  return useMutation<
    ContentVersionResponse,
    ApiClientError,
    { versionId: string; input: UpdateContentVersionInput }
  >({
    mutationFn: ({ versionId, input }) =>
      apiPatch<ContentVersionResponse>(`/api/admin/content-versions/${versionId}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.detail(contentItemId) });
    },
  });
}

/**
 * `POST /api/admin/content-versions/:id/publish` — flip a DRAFT
 * ContentVersion to PUBLISHED. The previously-published peer (if any)
 * is archived atomically by the service. 409 `VERSION_RACE` is surfaced
 * as `ApiClientError`; the page handler renders the "another publish
 * raced you" toast and explicitly refetches.
 */
export function usePublishContentVersion(contentItemId: string) {
  const qc = useQueryClient();
  return useMutation<
    PublishContentVersionResponse,
    ApiClientError,
    {
      versionId: string;
      /**
       * Issue #243. `"primary"` (default) flips DRAFT → PUBLISHED and
       * demotes the prior PUBLISHED peer to ARCHIVED. `"experiment"`
       * flips DRAFT → EXPERIMENT with no demotion — used when the
       * operator wants to expose a variant to experiment traffic only.
       */
      mode?: "primary" | "experiment";
      /**
       * When true, the server's `PUBLISH_BLOCKED_BY_FLAGS` gate is
       * skipped: any unresolved HIGH/CRITICAL flags are auto-resolved
       * with an audit-log entry recording the override. Operator opts in
       * from the confirmation dialog after seeing the flag list — the
       * human stays in control of publish, the judges remain advisory.
       */
      overrideFlags?: boolean;
    }
  >({
    mutationFn: ({ versionId, mode, overrideFlags }) =>
      apiPost<PublishContentVersionResponse>(`/api/admin/content-versions/${versionId}/publish`, {
        ...(mode !== undefined ? { mode } : {}),
        ...(overrideFlags === true ? { overrideFlags: true } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.detail(contentItemId) });
    },
  });
}

/**
 * `POST /api/admin/content-versions/:id/promote` — flip an EXPERIMENT
 * ContentVersion to PUBLISHED. The prior PUBLISHED peer (if any) is
 * demoted to ARCHIVED in the same transaction. Issue #243.
 */
export function usePromoteContentVersion(contentItemId: string) {
  const qc = useQueryClient();
  return useMutation<PromoteContentVersionResponse, ApiClientError, { versionId: string }>({
    mutationFn: ({ versionId }) =>
      apiPost<PromoteContentVersionResponse>(
        `/api/admin/content-versions/${versionId}/promote`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.detail(contentItemId) });
    },
  });
}

/**
 * `POST /api/admin/content-versions/:id/archive-experiment` — flip an
 * EXPERIMENT ContentVersion to ARCHIVED with no replacement. The
 * "we tested it, didn't win" action. Issue #243.
 */
export function useArchiveExperimentContentVersion(contentItemId: string) {
  const qc = useQueryClient();
  return useMutation<
    ArchiveExperimentContentVersionResponse,
    ApiClientError,
    { versionId: string }
  >({
    mutationFn: ({ versionId }) =>
      apiPost<ArchiveExperimentContentVersionResponse>(
        `/api/admin/content-versions/${versionId}/archive-experiment`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.detail(contentItemId) });
    },
  });
}

/**
 * `POST /api/admin/content-versions/:id/unpublish` — flip the
 * currently-PUBLISHED version to ARCHIVED with no replacement. After
 * this the item has no public version until a new draft is published.
 */
export function useUnpublishContentVersion(contentItemId: string) {
  const qc = useQueryClient();
  return useMutation<UnpublishContentVersionResponse, ApiClientError, { versionId: string }>({
    mutationFn: ({ versionId }) =>
      apiPost<UnpublishContentVersionResponse>(
        `/api/admin/content-versions/${versionId}/unpublish`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.detail(contentItemId) });
    },
  });
}

/**
 * `POST /api/admin/content-items/:id/versions/draft-from/:sourceVersionId` —
 * create a new DRAFT seeded from a PUBLISHED or ARCHIVED source. Two
 * operator workflows share this endpoint:
 *
 *   - "Create new draft from this" on the currently-published row —
 *     the editable amendment path.
 *   - "Rollback" on an archived row — same endpoint, archived source.
 *
 * The service rejects DRAFT sources with 422 `VERSION_NOT_ROLLBACKABLE`.
 */
export function useDraftFromContentVersion(contentItemId: string) {
  const qc = useQueryClient();
  return useMutation<ContentVersionResponse, ApiClientError, { sourceVersionId: string }>({
    mutationFn: ({ sourceVersionId }) =>
      apiPost<ContentVersionResponse>(
        `/api/admin/content-items/${contentItemId}/versions/draft-from/${sourceVersionId}`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.contentItems.detail(contentItemId) });
    },
  });
}
